import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  userAdminCreateSchema,
  userAdminUpdateSchema,
  type UserAdminSummary,
  type UserAdminCreateInput,
  type UserAdminUpdateInput,
  type RoleSchemaType,
} from '@ventin/shared';
import { useCreateUser, useUpdateUser, useCompanies } from '@/lib/adminApi';
import { generatePassword } from '@/lib/password';

type Mode = 'create' | 'edit';

interface Props {
  mode: Mode;
  initial?: UserAdminSummary;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-select this company for new CLIENT users (e.g. from CompanyDetailPage) */
  defaultCompanyId?: string;
}

interface FieldErrors {
  email?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  companyId?: string;
  password?: string;
  form?: string;
}

const ROLE_HELP =
  'CLIENT users must belong to a Company; ADMIN and SUPPORT users are platform-wide.';

/**
 * UserFormDialog — create/edit a platform user (USR-01..04).
 *
 * Role/company invariant (mirrors Plan 02-05 API enforcement):
 *   - role = CLIENT ⇒ companyId REQUIRED (picker visible)
 *   - role = ADMIN | SUPPORT ⇒ companyId MUST be null (picker hidden + cleared)
 *
 * Form matches the CompanyFormDialog / AgreementFormDialog patterns: local
 * useState, `safeParse()` against the Plan 02-02 schemas, toast on success,
 * inline form-level error on API failure.
 */
export function UserFormDialog({ mode, initial, open, onOpenChange, defaultCompanyId }: Props) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [sendLoginInfo, setSendLoginInfo] = useState(false);
  const [role, setRole] = useState<RoleSchemaType>('CLIENT');
  const [companyId, setCompanyId] = useState<string>('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [busy, setBusy] = useState(false);

  const createMut = useCreateUser();
  const updateMut = useUpdateUser();
  const { data: companiesData } = useCompanies();
  const companies = useMemo(() => companiesData?.companies ?? [], [companiesData]);

  // Re-sync form state when the dialog opens or the target changes.
  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (mode === 'edit' && initial) {
      setEmail(initial.email);
      setFirstName(initial.firstName ?? initial.fullName.split(/\s+/)[0] ?? '');
      setLastName(initial.lastName ?? initial.fullName.split(/\s+/).slice(1).join(' '));
      setRole(initial.role);
      setCompanyId(initial.companyId ?? '');
      setPassword('');
      setSendLoginInfo(false);
    } else {
      setEmail('');
      setFirstName('');
      setLastName('');
      setRole('CLIENT');
      setCompanyId(defaultCompanyId ?? '');
      setPassword('');
      setSendLoginInfo(false);
    }
  }, [open, mode, initial, defaultCompanyId]);

  // Invariant enforcement: whenever role leaves CLIENT, clear companyId so
  // the hidden field can't leak a stale assignment into the payload.
  useEffect(() => {
    if (role !== 'CLIENT' && companyId !== '') {
      setCompanyId('');
    }
  }, [role, companyId]);

  function buildCreatePayload(): { input?: UserAdminCreateInput; errs: FieldErrors } {
    // Client-side invariant: CLIENT requires company; ADMIN/SUPPORT must not have one.
    if (role === 'CLIENT' && !companyId) {
      return { errs: { companyId: 'CLIENT users must be assigned to a Company' } };
    }
    const raw: Record<string, unknown> = {
      email: email.trim(),
      fullName: `${firstName.trim()} ${lastName.trim()}`.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      role,
    };
    if (role === 'CLIENT') raw.companyId = companyId;
    if (password.trim()) raw.password = password;

    const parsed = userAdminCreateSchema.safeParse(raw);
    if (parsed.success) return { input: parsed.data, errs: {} };
    return { errs: flattenIssues(parsed.error.issues) };
  }

  function buildUpdatePayload(): { input?: UserAdminUpdateInput; errs: FieldErrors } {
    if (role === 'CLIENT' && !companyId) {
      return { errs: { companyId: 'CLIENT users must be assigned to a Company' } };
    }
    const raw: Record<string, unknown> = {
      fullName: `${firstName.trim()} ${lastName.trim()}`.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      role,
    };
    // CLIENT → set companyId; ADMIN/SUPPORT → set null to strip any prior assignment.
    raw.companyId = role === 'CLIENT' ? companyId : null;

    const parsed = userAdminUpdateSchema.safeParse(raw);
    if (parsed.success) return { input: parsed.data, errs: {} };
    return { errs: flattenIssues(parsed.error.issues) };
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (mode === 'create') {
      const { input, errs } = buildCreatePayload();
      if (!input) {
        setErrors(errs);
        return;
      }
      setErrors({});
      setBusy(true);
      try {
        await createMut.mutateAsync(input);
        toast.success('User created');
        onOpenChange(false);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        if (/email/i.test(message) && /(conflict|already|409|unique)/i.test(message)) {
          setErrors({ email: 'Email already in use' });
        } else {
          setErrors({ form: "Couldn't save — try again" });
          toast.error("Couldn't save — try again");
        }
      } finally {
        setBusy(false);
      }
      return;
    }

    // edit
    if (!initial) return;
    const { input, errs } = buildUpdatePayload();
    if (!input) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      await updateMut.mutateAsync({ id: initial.id, patch: input });
      toast.success('Changes saved');
      onOpenChange(false);
    } catch {
      setErrors({ form: "Couldn't save — try again" });
      toast.error("Couldn't save — try again");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Create User' : 'Edit User'}</DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Add a new platform user. Leave password blank to have the user reset via email.'
              : 'Update this user. Email cannot be changed — users reset their own via email.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'create' ? (
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={!!errors.email}
              />
              {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Email</Label>
              <p className="text-sm text-muted-foreground">{email}</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First name</Label>
              <Input
                id="firstName"
                name="firstName"
                type="text"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                aria-invalid={!!errors.firstName}
              />
              {errors.firstName && <p className="text-xs text-destructive">{errors.firstName}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last name</Label>
              <Input
                id="lastName"
                name="lastName"
                type="text"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                aria-invalid={!!errors.lastName}
              />
              {errors.lastName && <p className="text-xs text-destructive">{errors.lastName}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="role">Role</Label>
            <select
              id="role"
              name="role"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
              value={role}
              onChange={(e) => setRole(e.target.value as RoleSchemaType)}
              aria-invalid={!!errors.role}
            >
              <option value="CLIENT">CLIENT</option>
              <option value="SUPPORT">SUPPORT</option>
              <option value="ADMIN">ADMIN</option>
            </select>
            <p className="text-xs text-muted-foreground">{ROLE_HELP}</p>
            {errors.role && <p className="text-xs text-destructive">{errors.role}</p>}
          </div>

          {role === 'CLIENT' && (
            <div className="space-y-2">
              <Label htmlFor="companyId">Company</Label>
              <select
                id="companyId"
                name="companyId"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                aria-invalid={!!errors.companyId}
              >
                <option value="">Select a company…</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {errors.companyId && <p className="text-xs text-destructive">{errors.companyId}</p>}
            </div>
          )}

          {mode === 'create' && (
            <div className="space-y-2">
              <Label htmlFor="password">Password (optional, min 12 chars)</Label>
              <div className="flex gap-2">
                <Input
                  id="password"
                  name="password"
                  type="text"
                  minLength={12}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-invalid={!!errors.password}
                  placeholder="Leave blank to send reset email"
                  autoComplete="off"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPassword(generatePassword())}
                >
                  Generate
                </Button>
              </div>
              {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
              <label className="flex items-center gap-2 text-sm pt-1">
                <input
                  type="checkbox"
                  checked={sendLoginInfo}
                  onChange={(e) => setSendLoginInfo(e.target.checked)}
                  className="h-4 w-4 rounded border-input accent-[#1924a2]"
                />
                Send login info by email
              </label>
              {sendLoginInfo && (
                <p className="text-xs text-muted-foreground">
                  Email delivery is coming soon — for now, copy the password and share it securely.
                </p>
              )}
            </div>
          )}

          {errors.form && (
            <Alert variant="destructive">
              <AlertDescription>{errors.form}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : mode === 'create' ? 'Create' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function flattenIssues(issues: { path: (string | number)[]; message: string }[]): FieldErrors {
  const errs: FieldErrors = {};
  for (const issue of issues) {
    const key = issue.path[0] as keyof FieldErrors | undefined;
    if (key && !errs[key]) errs[key] = issue.message;
  }
  return errs;
}

/** Exported so UsersPage can echo the same help copy in any inline hint. */
export const USER_ROLE_HELP_TEXT = ROLE_HELP;
